import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { TID } from "../../lib/testIds";

const CASES = [
  {
    tag: "SalesTech / MarTech",
    title: "AI-Powered Sales & Content Technology",
    summary:
      "Engineering intelligent content, search, and lead-generation workflows for a modern SaaS product.",
    caps: ["AI Product Engineering", "Natural-Language Search", "SaaS Development"],
    img: "https://images.pexels.com/photos/27141316/pexels-photo-27141316.jpeg",
    slug: "ai-sales-content-technology",
    span: "lg:col-span-8 lg:row-span-2",
  },
  {
    tag: "Logistics / Freight",
    title: "Freight Technology Platform",
    summary:
      "Building connected software workflows for complex freight and operational processes.",
    caps: ["Cloud Platforms", "System Integration", "Operational Dashboards"],
    img: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBsb2dpc3RpY3MlMjBkYXNoYm9hcmR8ZW58MHx8fHwxNzgyNTcyODU4fDA&ixlib=rb-4.1.0&q=85",
    slug: "logistics-freight-technology",
    span: "lg:col-span-4 lg:row-span-1",
  },
  {
    tag: "IoT / Monitoring",
    title: "IoT Monitoring & Operational Visibility",
    summary:
      "Connecting device data, monitoring workflows, and software systems for operational visibility.",
    caps: ["IoT Integration", "Backend Engineering", "Data Workflows"],
    img: "https://images.unsplash.com/photo-1670689334799-cdc6777db8cc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwyfHxpbmR1c3RyaWFsJTIwaW90JTIwZmFjdG9yeSUyMGRhcmt8ZW58MHx8fHwxNzgzMjQyNjcyfDA&ixlib=rb-4.1.0&q=85",
    slug: "iot-monitoring-operational-visibility",
    span: "lg:col-span-4 lg:row-span-1",
  },
];

const Card = ({ c, i }) => (
  <motion.article
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.2 }}
    transition={{ duration: 0.7, delay: i * 0.1 }}
    className={c.span}
  >
    <Link
      to={`/work/${c.slug}`}
      data-testid={TID.caseStudyCard(i)}
      className="group relative overflow-hidden border border-white/10 bg-black min-h-[360px] md:min-h-[420px] h-full flex flex-col"
    >
      <div className="absolute inset-0">
        <img
          src={c.img}
          alt={c.title}
          className="w-full h-full object-cover opacity-40 group-hover:opacity-60 transition-opacity duration-700 grayscale group-hover:grayscale-0"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/20" />
      </div>

      <div className="relative z-10 mt-auto p-6 md:p-8 flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FF5F1F]">
            {String(i + 1).padStart(2, "0")} / {c.tag}
          </span>
          <ArrowUpRight className="w-5 h-5 text-white/50 group-hover:text-[#FF5F1F] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all" />
        </div>
        <h3 className="font-headings text-2xl md:text-3xl lg:text-4xl font-bold tracking-tight text-white leading-tight max-w-xl">
          {c.title}
        </h3>
        <p className="text-sm md:text-base text-zinc-300 leading-relaxed max-w-lg">{c.summary}</p>
        <div className="flex flex-wrap gap-2 mt-2">
          {c.caps.map((cap, ci) => (
            <span
              key={ci}
              className="font-mono text-[10px] uppercase tracking-[0.15em] px-2.5 py-1 border border-white/15 text-zinc-300"
            >
              {cap}
            </span>
          ))}
        </div>
      </div>
    </Link>
  </motion.article>
);

export const FeaturedWork = () => {
  return (
    <section
      id="work"
      data-testid={TID.featuredWork}
      className="relative bg-[#050505] py-24 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14 md:mb-20">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-px w-8 bg-[#FF5F1F]" />
              <span className="font-mono text-[10px] md:text-xs uppercase tracking-[0.25em] text-[#FF5F1F]">
                Selected Product Experience
              </span>
            </div>
            <h2 className="font-headings text-4xl md:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05] text-white">
              Complex Products. Real Engineering. <span className="text-zinc-500">Business Context.</span>
            </h2>
          </div>
          <p className="text-sm md:text-base text-zinc-400 max-w-md leading-relaxed">
            Our work spans SaaS platforms, AI-enabled products, data-intensive systems,
            operational software, workflow automation, mobile applications, IoT, and cloud
            platforms. Every engagement begins with the problem, not the technology stack.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-6 auto-rows-[380px]">
          {CASES.map((c, i) => (
            <Card key={i} c={c} i={i} />
          ))}
        </div>
      </div>
    </section>
  );
};
